import React from 'react'
import p from 'prop-types'
import { connect } from 'react-redux'
import { CommentBox } from './CommentBox'
import { Comment } from './Comment'
import styles from './Comments.scss'
import cx from 'classnames'
import { Button } from 'common/Button'
import { Icon } from 'common/Icon'
import * as commentsActions from 'lib/api/comments'
import { Msg, fm, commonNouns, commonCTAs } from 'lib/localization'
import { FormattedMessage as T, injectIntl } from 'react-intl'
import _ from 'lodash'

class Comments extends React.PureComponent {
  static propTypes = {
    deleteComment: p.func.isRequired,
    submitComment: p.func.isRequired,
    loadComments: p.func.isRequired,
    loadOnMount: p.bool,
    reverseOrder: p.bool,
    showTime: p.bool
  }

  static defaultProps = {
    includeHeading: true,
    loadOnMount: true,
    reverseOrder: false,
    showTime: false
  }

  constructor (props) {
    super(props)
    this.state = {
      expanded: props.defaultExpanded || false,
      canExpand: true
    }
  }

  componentDidMount () {
    const { loadOnMount, loadComments } = this.props
    loadOnMount && loadComments()

    this.checkIfCollapse()
  }

  componentDidUpdate () {
    this.checkIfCollapse()
  }

  checkIfCollapse = () => {
    const { expanded, canExpand } = this.state
    if (!this._collapseContainer && this.props.comments) { return }
    // note that none of these checks work when expanded is true because we're
    // relying on the CSS max-height being applied to do DOM measurement.
    // To bypass this limitation we'd have to render an invisible shadow copy
    // of the component when expanded to do height detection, which sucks

    if (canExpand && !expanded && this._collapseContainer.clientHeight === this._collapseContainer.scrollHeight) {
      // if we think we can expand but we can't, update state
      this.setState({ canExpand: false })
    }
    if (!canExpand && !expanded && this._collapseContainer.clientHeight !== this._collapseContainer.scrollHeight) {
      // if we think we can't expand but we can, update state
      this.setState({ canExpand: true })
    }
  }

  expand = () => this.setState({ expanded: true })

  collapse = () => this.setState({ expanded: false })

  render () {
    const { expanded, canExpand } = this.state
    const {
      comments, currentUser, canModerate, submitComment, deleteComment,
      includeHeading, reverseOrder, showTime, placeholder, className, readOnly,
      intl
    } = this.props

    const commentsHeading = comments && comments.size > 0
      ? `${comments.size} ${fm(intl, commonNouns.comments, { count: comments.size })}`
      : <T id='Comments.beFirst' defaultMessage='Be the first to leave a comment' />

    const commentsToShow = comments && reverseOrder ? comments.reverse() : comments

    return (
      <div className={cx(styles.Comments, expanded && styles.expanded, className)}>
        <div className={cx(!expanded && comments && comments.size > 0 && styles.collapseContainer, !canExpand && styles.noScrim)} ref={ref => { this._collapseContainer = ref }}>
          {includeHeading && <div className={styles.heading}>{commentsHeading}</div>}
          {!readOnly && <CommentBox loggedIn={!!currentUser.id} onSubmit={submitComment} placeholder={placeholder} intl={intl} />}
          {commentsToShow && commentsToShow.map(comment => (
            <Comment
              key={comment.id}
              comment={comment}
              onDelete={() => deleteComment(comment.id)}
              showTime={showTime}
              canDelete={canModerate || currentUser.id === comment.user_id} />
          )).toArray()}
        </div>
        {commentsToShow && commentsToShow.size > 0 && canExpand && <div className={styles.centerButton}>
          {!expanded && <Button type='default' className={styles.expandButton} onClick={this.expand}>
            <Icon i='ios-arrow-down' /> <Msg m={commonCTAs.more} values={{ things: _.capitalize(intl.formatMessage(commonNouns.comments, { count: 0 })) }} />
          </Button>}
          {expanded && <Button type='default' className={styles.collapseButton} onClick={this.collapse}>
            <Icon i='ios-arrow-up' /> <Msg m={commonCTAs.fewer} values={{ things: _.capitalize(intl.formatMessage(commonNouns.comments, { count: 0 })) }} />
          </Button>}
        </div>}
      </div>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const { objectType, objectId } = ownProps
  return {
    currentUser: state.currentUser,
    comments: state.comments.getIn([objectType, objectId, 'comments'])
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const { objectType, objectId, privacyCode } = ownProps

  return {
    loadComments: () => dispatch(commentsActions.loadComments(objectType, objectId, privacyCode)),
    deleteComment: (commentId) => dispatch(commentsActions.deleteComment(objectType, objectId, commentId)),
    submitComment: (comment) => dispatch(commentsActions.submitComment(objectType, objectId, comment))
  }
}

export const CommentsContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(Comments))

CommentsContainer.propTypes = {
  objectType: p.string.isRequired,
  objectId: p.number.isRequired
}
